import { apiClient } from './api';
import {
  SocialMediaSession,
  StartSocialSessionRequest,
  StartSocialSessionResponse,
  SocialIterationRequest,
  SocialIterationResponse,
  BulkSocialIterationRequest,
  BulkSocialIterationResponse,
  MultiSocialRequest,
  MultiSocialResponse,
  RefineSessionRequest,
  RefineSessionResponse,
  SessionListResponse
} from '../models/socialMedia';

const STORAGE_KEY = 'social_media_sessions';

export class SocialMediaService {
  // Local Storage Management
  static loadSessions(): SocialMediaSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const sessions = JSON.parse(stored);
    // Convert date strings back to Date objects
    return sessions.map((session: any) => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastUpdatedAt: new Date(session.lastUpdatedAt),
      iterations: session.iterations.map((iter: any) => ({
        ...iter,
        timestamp: new Date(iter.timestamp)
      }))
    }));
  }

  static saveSession(session: SocialMediaSession): void {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.loadSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  // API Methods - Multi-Agent Approach (recommended)
  static async startMultiAgentSession(request: MultiSocialRequest): Promise<MultiSocialResponse> {
    // Map campaignDescription to campaign as expected by the API
    const apiRequest = {
      campaign: request.campaignDescription,
      targetPlatforms: request.targetPlatforms,
      targetAudience: request.targetAudience,
      brandVoice: request.brandVoice,
      postCountPerPlatform: request.postCountPerPlatform,
      callToAction: request.callToAction
    };
    const response = await apiClient.post('/api/multi-agent/social/start', apiRequest);
    return response.data;
  }

  // API Methods - Session-based approach (for Content Marketing Controller)
  static async startSession(request: StartSocialSessionRequest): Promise<StartSocialSessionResponse> {
    const response = await apiClient.post('/api/marketing/session/start', {
      prompt: request.campaignDescription,
      parameters: {
        targetPlatforms: request.targetPlatforms,
        targetAudience: request.targetAudience,
        brandVoice: request.brandVoice,
        postCountPerPlatform: request.postCountPerPlatform,
        callToAction: request.callToAction
      }
    });
    return response.data;
  }

  static async generateIteration(
    sessionId: string, 
    request: SocialIterationRequest
  ): Promise<SocialIterationResponse> {
    const response = await apiClient.post(
      `/api/marketing/session/${sessionId}/iterate`,
      {
        agentType: request.agentType,
        userInput: request.userInput,
        additionalInstructions: request.additionalInstructions,
        temperature: request.temperature
      }
    );
    return response.data;
  }

  static async bulkIterate(
    sessionId: string,
    request: BulkSocialIterationRequest
  ): Promise<BulkSocialIterationResponse> {
    const response = await apiClient.post(
      `/api/marketing/session/${sessionId}/bulk-iterate`,
      request
    );
    return response.data;
  }

  static async getSession(sessionId: string): Promise<any> {
    const response = await apiClient.get(`/api/marketing/session/${sessionId}`);
    return response.data;
  }

  // New API methods for BigQuery session management
  static async getMultiAgentSession(sessionId: string): Promise<any> {
    const response = await apiClient.get(`/api/multi-agent/social/session/${sessionId}`);
    return response.data;
  }

  static async refineSession(sessionId: string, request: RefineSessionRequest): Promise<RefineSessionResponse> {
    // Send the request properties directly (not nested under 'request')
    const payload = {
      refinementPrompt: request.refinementPrompt,
      targetPlatforms: request.targetPlatforms || [],
      regenerateStrategy: request.regenerateStrategy || false
    };
    const response = await apiClient.post(`/api/multi-agent/social/session/${sessionId}/refine`, payload);
    return response.data;
  }

  static async listSessions(): Promise<SessionListResponse> {
    const response = await apiClient.get('/api/multi-agent/social/sessions');
    return response.data;
  }

  // Utility Methods
  static exportSession(session: SocialMediaSession): void {
    const dataStr = JSON.stringify(session, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `social-media-session-${session.id}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  static async importSession(file: File): Promise<SocialMediaSession> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const session = JSON.parse(e.target?.result as string);
          // Convert date strings to Date objects
          session.createdAt = new Date(session.createdAt);
          session.lastUpdatedAt = new Date(session.lastUpdatedAt);
          session.iterations = session.iterations.map((iter: any) => ({
            ...iter,
            timestamp: new Date(iter.timestamp)
          }));
          
          // Generate new ID for imported session
          session.id = `imported-${Date.now()}`;
          
          this.saveSession(session);
          resolve(session);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }

  // Helper method to parse platform posts from API response
  static parsePlatformPosts(platformPosts: { [key: string]: string[] }): any {
    const parsed: any = {};
    for (const [platform, posts] of Object.entries(platformPosts)) {
      parsed[platform] = posts.map(content => ({
        platform,
        content,
        hashtags: this.extractHashtags(content),
        characterCount: content.length,
        mediaType: 'text' as const
      }));
    }
    return parsed;
  }

  static extractHashtags(content: string): string[] {
    const hashtagRegex = /#\w+/g;
    return content.match(hashtagRegex) || [];
  }

  // Missing methods that are used in the component
  static async iterateSession(
    sessionId: string, 
    request: SocialIterationRequest
  ): Promise<SocialIterationResponse> {
    // Ensure refinementPrompt is not empty
    const refinementPrompt = request.userInput?.trim() || 
      `Continue developing content for ${request.agentType} with the current campaign strategy`;
    
    // Use the refine endpoint for iterations
    const response = await this.refineSession(sessionId, {
      refinementPrompt,
      targetPlatforms: request.targetPlatforms
    });
    
    // Convert RefineSessionResponse to SocialIterationResponse format
    return {
      iterationId: Date.now().toString(),
      agentType: request.agentType,
      output: 'Iteration completed successfully',
      platformPosts: response.refinedPosts,
      hashtags: response.hashtagSuggestions
    };
  }

  static updateSession(session: SocialMediaSession): void {
    this.saveSession(session);
  }

  static copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Unable to copy to clipboard', err);
      }
      document.body.removeChild(textArea);
    }
  }
}